// The browser half of the secrets registry: the entry types, the presence
// presets the client entries share, and every NEXT_PUBLIC_* variable. Client
// code reaches the registry only through this module, so the browser bundle
// never carries the names or descriptions of server secrets.
// `secrets.registry.ts` adds the server entries and is never imported by
// client code.

import { z } from 'zod';

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

/**
 * Per Doppler-config presence for one secret:
 *   - 'absent'  — secret should NOT exist in this Doppler config.
 *   - 'local'   — secret exists in Doppler but is NOT pushed to Vercel
 *                 (operator/admin tokens, or vars used only by scripts).
 *   - production / preview / development — secret exists in Doppler AND
 *     is pushed to the named Vercel environment by the reconciler.
 */
export type ConfigPresence = 'absent' | 'local' | VercelEnvironment;

export type DopplerConfig = 'dev' | 'prd' | 'stg';

export type SecretEntry<TSchema extends z.ZodType = z.ZodType> = {
  description: string;
  presence: Record<DopplerConfig, ConfigPresence>;
  schema: TSchema;
  scope: SecretScope;
};

export type SecretScope = 'client' | 'server';

export type VercelEnvironment = 'development' | 'preview' | 'production';

// ----------------------------------------------------------------------
// Presence presets shared with the server entries. `as const` keeps the
// literal types ('local' | 'preview' etc.) that pickSchemas relies on.
// ----------------------------------------------------------------------

/** Standard runtime app secret: exists in all 3 Doppler configs, syncs to
 *  Vercel preview/production. Local dev uses `doppler run -c dev`. */
export const RUNTIME = {
  dev: 'local',
  prd: 'production',
  stg: 'preview',
} as const satisfies Record<DopplerConfig, ConfigPresence>;

/** Vercel-injected at runtime (VERCEL_ENV, VERCEL_URL, etc.). Not in
 *  Doppler, just validated by env.ts so the runtime app can read it
 *  with type safety. */
export const VERCEL_INJECTED = {
  dev: 'absent',
  prd: 'absent',
  stg: 'absent',
} as const satisfies Record<DopplerConfig, ConfigPresence>;

/** Persistent staging credentials are separate from per-PR preview branches. */
export const SUPABASE_RUNTIME = {
  dev: 'local',
  prd: 'production',
  stg: 'preview',
} as const satisfies Record<DopplerConfig, ConfigPresence>;

// ----------------------------------------------------------------------
// Client entries, sorted alphabetically by name.
// ----------------------------------------------------------------------

export const CLIENT_SECRETS = {
  NEXT_PUBLIC_APP_VERSION: {
    description:
      'Git commit SHA the build was made from; set by next.config.ts from VERCEL_GIT_COMMIT_SHA. A browser tab compares its own value with /api/app-version to detect it runs an older deployment.',
    presence: VERCEL_INJECTED,
    schema: z.string().min(1).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: {
    description: 'Google Maps JavaScript API key for the browser map component.',
    presence: RUNTIME,
    schema: z.string().min(1).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: {
    description: 'Public Google Tag Manager web container id for production acquisition analytics.',
    presence: { dev: 'absent', prd: 'production', stg: 'absent' },
    schema: z.string().startsWith('GTM-').optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_META_APP_ID: {
    description: 'Public Meta app id used by client-side Facebook Login SDK.',
    presence: RUNTIME,
    schema: z.string().min(1).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_META_BUSINESS_PORTFOLIO_ID: {
    description: 'Meta Business Portfolio external id used by client embedded-signup flow.',
    presence: RUNTIME,
    schema: z.string().min(1).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_META_FB_LOGIN_CONFIG_ID: {
    description: 'Meta Facebook Login for Business v4 configuration id for embedded signup.',
    presence: RUNTIME,
    schema: z.string().min(1).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_META_PIXEL_ID: {
    description: 'Public Meta Pixel id for website acquisition analytics.',
    presence: { dev: 'absent', prd: 'production', stg: 'absent' },
    schema: z.string().regex(/^\d+$/).optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_POSTHOG_HOST: {
    description: 'PostHog API host for browser analytics.',
    presence: RUNTIME,
    schema: z.url().optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_POSTHOG_KEY: {
    description: 'Public PostHog project token for browser analytics.',
    presence: RUNTIME,
    schema: z.string().startsWith('phc_').optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_SENTRY_DSN: {
    description: 'Sentry DSN for the client-side bundle (browser errors).',
    presence: RUNTIME,
    schema: z.url().optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_SENTRY_ENVIRONMENT: {
    description: 'Build-derived Sentry environment shared with the server; set by next.config.ts.',
    presence: RUNTIME,
    schema: z.string().optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: {
    description: 'Stripe publishable key for the browser SDK.',
    presence: RUNTIME,
    schema: z.string().startsWith('pk_').optional(),
    scope: 'client',
  },

  NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: {
    description: 'Supabase publishable (anon) key for the browser Supabase client. Required.',
    presence: SUPABASE_RUNTIME,
    schema: z.string().min(1),
    scope: 'client',
  },

  NEXT_PUBLIC_SUPABASE_URL: {
    description: 'Supabase project URL for the browser Supabase client. Required.',
    presence: SUPABASE_RUNTIME,
    schema: z.url(),
    scope: 'client',
  },
} as const satisfies Record<`NEXT_PUBLIC_${string}`, SecretEntry>;

/**
 * NEXT_PUBLIC_* values must be referenced LITERALLY for Next.js's static
 * inliner to embed them in the client bundle; a dynamic `process.env[name]`
 * lookup is undefined in the browser. The `satisfies` clause fails the
 * typecheck when a client entry is missing here.
 */
export const clientRuntimeEnv = {
  NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
  NEXT_PUBLIC_META_APP_ID: process.env.NEXT_PUBLIC_META_APP_ID,
  NEXT_PUBLIC_META_BUSINESS_PORTFOLIO_ID: process.env.NEXT_PUBLIC_META_BUSINESS_PORTFOLIO_ID,
  NEXT_PUBLIC_META_FB_LOGIN_CONFIG_ID: process.env.NEXT_PUBLIC_META_FB_LOGIN_CONFIG_ID,
  NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
  NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
} satisfies Record<keyof typeof CLIENT_SECRETS, string | undefined>;

type ClientSchemas = {
  [K in keyof typeof CLIENT_SECRETS]: (typeof CLIENT_SECRETS)[K]['schema'];
};

export function pickClientSchemas(): ClientSchemas {
  const out: Record<string, z.ZodType> = {};
  for (const [name, entry] of Object.entries(CLIENT_SECRETS)) {
    out[name] = entry.schema;
  }
  return out as never;
}
